import { makeAutoObservable, toJS } from 'mobx';
import { IPointHeaderData } from 'entities/business/interfaces/point-card.interface';
import { IWeekWorkTime } from 'features/working-hours-picker/interfaces/time-table-input.interface';
import { businessStore } from 'entities/business/model/business-card.store';
import { IUpdatePointDto } from 'shared/api/business/interfaces/update-point-info-dto.interface';
import { validateForm } from 'widgets/business-settings/model/validate-form';

class PointSettingsStore {
  pointId: string | null = null;

  // Form fields
  name = '';
  address = '';
  contactPhone = '';
  siteUrl = '';
  description = '';

  // Working hours state
  isEmployeeMode = false;
  isSwitchActive = false;
  localWeekWorkTime: IWeekWorkTime | null = null;

  validationError: string | undefined = undefined;

  constructor() {
    makeAutoObservable(this);
  }

  initFromPointData(data: IPointHeaderData) {
    this.pointId = data.id;
    this.name = data.name || '';
    this.address = data.address || '';
    this.contactPhone = data.contactPhone || '';
    this.siteUrl = data.siteUrl || '';
    this.description = data.description || '';
    this.isEmployeeMode = data.isWorkTimeByEmployee;
    this.isSwitchActive = data.isWorkTimeByEmployee;
    this.localWeekWorkTime = data.weekWorkTime
      ? { ...data.weekWorkTime }
      : null;
    this.validationError = undefined;
  }

  setField<
    K extends 'name' | 'address' | 'contactPhone' | 'siteUrl' | 'description',
  >(field: K, value: string) {
    this[field] = value;
  }

  setWorkingHours(mode: 'employee' | 'fixed', value: IWeekWorkTime | null) {
    this.isEmployeeMode = mode === 'employee';
    this.localWeekWorkTime = value;
  }

  setSwitchActive(active: boolean) {
    this.isSwitchActive = active;
  }

  async save() {
    if (this.isEmployeeMode && !this.isSwitchActive) {
      this.validationError =
        'выберите другой режим графика работы точки "каждый день" или "неделя"';
      throw new Error(this.validationError);
    }

    const fieldError = validateForm({
      name: this.name,
      address: this.address,
      phone: this.contactPhone,
      siteUrl: this.siteUrl,
      description: this.description,
    });
    if (fieldError) {
      this.validationError = fieldError;
      throw new Error(fieldError);
    }

    if (!this.pointId) {
      this.validationError = 'ID точки не найден';
      throw new Error('ID точки не найден');
    }

    const data: IUpdatePointDto = {
      id: this.pointId as string & { __brand: 'UUID' },
      currentTime: new Date(),
      name: this.name,
      address: this.address,
      contactPhone: this.contactPhone,
      siteUrl: this.siteUrl,
      description: this.description,
      weekWorkTime: this.isEmployeeMode ? null : toJS(this.localWeekWorkTime),
    };

    try {
      await businessStore.updatePointHeaderData(data);
      if (businessStore.pointHeaderData) {
        this.initFromPointData(businessStore.pointHeaderData);
      }
      this.validationError = undefined;
    } catch (e) {
      if (e instanceof Error) {
        this.validationError = e.message;
      }
      throw e;
    }
  }

  reset() {
    this.pointId = null;
    this.name = '';
    this.address = '';
    this.contactPhone = '';
    this.siteUrl = '';
    this.description = '';
    this.isEmployeeMode = false;
    this.isSwitchActive = false;
    this.localWeekWorkTime = null;
    this.validationError = undefined;
  }
}

export const pointSettingsStore = new PointSettingsStore();
